import { useEffect, useMemo, useState } from 'react';
import { ref, remove } from 'firebase/database';
import { FaCheck, FaCreditCard, FaPen, FaTrash } from 'react-icons/fa';
import Button from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import {
  WorkspaceEmptyState,
  WorkspaceRail,
  WorkspaceSection,
  WorkspaceSelectableList,
  WorkspaceShell,
  WorkspaceStat,
  WorkspaceStats,
  WorkspaceStatusChip,
} from '../ui/Workspace';
import RecordPaymentModal from './RecordPaymentModal';
import CreatePaymentRequestModal from './CreatePaymentRequestModal';
import PayModal from './PayModal';
import VerifyPaymentModal from './VerifyPaymentModal';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import {
  useAdminPaymentsOverview,
  useStudentPayments,
} from '../../hooks/useLmsData';
import { verifyPaymentRecord } from '../../utils/lmsMutations';
import { formatAmount, getStatusBadgeClass } from './utils';
import {
  StudentActionLink,
  StudentBadge,
  StudentCard,
  StudentEmpty,
  StudentGridStats,
  StudentPage,
  StudentSection,
  StudentStatCard,
} from '../student/StudentUI';
import {
  formatStudentCurrency,
  formatStudentDate,
} from '../../utils/studentExperience';

const PaymentsPage = () => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const adminResult = useAdminPaymentsOverview({ enabled: isAdmin });
  const studentResult = useStudentPayments(currentUser?.uid, { enabled: !isAdmin });
  const { data, loading, error, refresh } = isAdmin ? adminResult : studentResult;
  const payments = useMemo(() => data?.payments || [], [data]);
  const paymentRequests = useMemo(() => data?.paymentRequests || [], [data]);

  const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isPaymentRequestModalOpen, setIsPaymentRequestModalOpen] =
    useState(false);
  const [selectedPaymentRequest, setSelectedPaymentRequest] = useState(null);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [paymentToVerify, setPaymentToVerify] = useState(null);
  const [selectedQueueId, setSelectedQueueId] = useState(
    isAdmin ? 'pending-verification' : 'open-requests',
  );
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    studentName: '',
    batchId: '',
  });

  const filteredPayments = useMemo(
    () =>
      payments.filter((payment) => {
        const matchesDate =
          (!filters.startDate || payment.paymentDate >= filters.startDate) &&
          (!filters.endDate || payment.paymentDate <= filters.endDate);
        const matchesStudent =
          !filters.studentName ||
          payment.studentName
            ?.toLowerCase()
            .includes(filters.studentName.toLowerCase());
        const matchesBatch =
          !filters.batchId ||
          payment.batchName?.toLowerCase().includes(filters.batchId.toLowerCase());

        return matchesDate && matchesStudent && matchesBatch;
      }),
    [filters, payments],
  );

  const filteredRequests = useMemo(
    () =>
      paymentRequests.filter((request) => {
        const matchesDate =
          (!filters.startDate ||
            new Date(request.dueDate) >= new Date(filters.startDate)) &&
          (!filters.endDate ||
            new Date(request.dueDate) <= new Date(filters.endDate));
        const matchesStudent =
          !filters.studentName ||
          request.studentName
            ?.toLowerCase()
            .includes(filters.studentName.toLowerCase());
        const matchesBatch =
          !filters.batchId ||
          request.batchName?.toLowerCase().includes(filters.batchId.toLowerCase());

        return matchesDate && matchesStudent && matchesBatch;
      }),
    [filters, paymentRequests],
  );

  const queueItems = isAdmin
    ? [
        {
          id: 'pending-verification',
          label: 'Pending Verification',
          description: 'Proofs waiting for admin review',
          status: 'pending',
          count: filteredPayments.filter((payment) => payment.status === 'pending')
            .length,
        },
        {
          id: 'open-requests',
          label: 'Open Requests',
          description: 'Requests still waiting for payment',
          status: 'planning',
          count: filteredRequests.filter((request) => request.status === 'pending')
            .length,
        },
        {
          id: 'history',
          label: 'Payment History',
          description: 'Completed or declined records',
          status: 'completed',
          count: filteredPayments.filter((payment) => payment.status !== 'pending')
            .length,
        },
      ]
    : [
        {
          id: 'open-requests',
          label: 'Pay Now',
          description: 'Requests that still need your payment',
          status: 'planning',
          count: filteredRequests.filter((request) => request.status === 'pending')
            .length,
        },
        {
          id: 'awaiting-review',
          label: 'Awaiting Review',
          description: 'Receipts you already submitted',
          status: 'payment_review',
          count: filteredPayments.filter((payment) => payment.status === 'pending')
            .length,
        },
        {
          id: 'history',
          label: 'Payment History',
          description: 'Completed payment trail',
          status: 'completed',
          count: filteredPayments.filter((payment) => payment.status === 'completed')
            .length,
        },
      ];

  const currentQueue = queueItems.find((item) => item.id === selectedQueueId) || queueItems[0];

  const currentRequests = filteredRequests.filter((request) => {
    if (currentQueue?.id === 'open-requests') return request.status === 'pending';
    if (currentQueue?.id === 'history') return request.status !== 'pending';
    return true;
  });

  const currentPayments = filteredPayments.filter((payment) => {
    if (currentQueue?.id === 'pending-verification') return payment.status === 'pending';
    if (currentQueue?.id === 'awaiting-review') return payment.status === 'pending';
    if (currentQueue?.id === 'history') return payment.status !== 'pending';
    return false;
  });

  const stats = {
    pendingPayments: payments.filter((payment) => payment.status === 'pending')
      .length,
    pendingRequests: paymentRequests.filter((request) => request.status === 'pending')
      .length,
    completedPayments: payments.filter((payment) => payment.status === 'completed')
      .length,
    totalAmount: payments
      .filter((payment) => payment.status === 'completed')
      .reduce((sum, payment) => sum + Number(payment.amount || 0), 0),
  };

  const handleEditPayment = (payment) => {
    setSelectedPayment(payment);
    setIsRecordModalOpen(true);
  };

  const handleDelete = async (item, type) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this item? This action cannot be undone.',
      )
    ) {
      return;
    }

    if (type === 'payment') {
      await remove(ref(db, `payments/${item.id}`));
      if (item.status === 'completed') {
        await remove(ref(db, `verifiedPayments/${item.id}`));
      }
    } else {
      await remove(ref(db, `paymentRequests/${item.id}`));
    }

    refresh();
  };

  const handleVerifyPayment = async (payment, isApproved) => {
    await verifyPaymentRecord({
      payment,
      adminUser: currentUser,
      isApproved,
    });
    refresh();
    setPaymentToVerify(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-8 text-red-500">{error.message}</div>;
  }

  if (!isAdmin) {
    const openRequests = paymentRequests.filter((request) => request.status === 'pending');
    const reviewPayments = payments.filter((payment) => payment.status === 'pending');
    const completedPayments = payments.filter((payment) => payment.status === 'completed');

    return (
      <>
        <StudentPage
          title="Payments"
          subtitle="Your payment steps are shown in simple cards so you can see what is waiting, what was sent, and what is finished."
        >
          <StudentGridStats>
            <StudentStatCard
              label="Pay now"
              value={openRequests.length}
              detail="Requests waiting for your receipt."
              tone="warning"
            />
            <StudentStatCard
              label="Under review"
              value={reviewPayments.length}
              detail="Receipts the academy is checking."
            />
            <StudentStatCard
              label="Completed"
              value={completedPayments.length}
              detail="Payments already approved."
              tone="success"
            />
            <StudentStatCard
              label="Paid total"
              value={formatStudentCurrency(
                completedPayments.reduce(
                  (sum, payment) => sum + Number(payment.amount || 0),
                  0,
                ),
              )}
              detail="Approved payment total."
              tone="success"
            />
          </StudentGridStats>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,1.1fr)_24rem]">
            <div className="space-y-5">
              <StudentSection
                title="Pay now"
                description="When the academy sends a request, it appears here with one clear action."
              >
                {!openRequests.length ? (
                  <StudentEmpty
                    title="No payment request right now"
                    message="Your next request will appear here."
                  />
                ) : (
                  <div className="space-y-4">
                    {openRequests.map((request) => (
                      <StudentCard key={request.id} className="bg-white">
                        <div className="space-y-4">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="text-lg font-semibold text-stone-900">
                                {request.courseName || 'Course payment'}
                              </p>
                              <p className="mt-1 text-sm text-stone-500">
                                Due {formatStudentDate(request.dueDate)}
                              </p>
                            </div>
                            <StudentBadge tone="warning">Pay now</StudentBadge>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <PaymentInfo label="Amount" value={formatStudentCurrency(request.amount)} />
                            <PaymentInfo label="Batch" value={request.batchName || '-'} />
                            <PaymentInfo label="Type" value={request.paymentType || 'Payment'} />
                            <PaymentInfo
                              label="Installment"
                              value={
                                request.installmentNumber
                                  ? `${request.installmentNumber}/${request.totalInstallments}`
                                  : 'One time'
                              }
                            />
                          </div>
                          <div className="flex justify-end">
                            <Button
                              onClick={() => {
                                setSelectedPaymentRequest(request);
                                setIsPayModalOpen(true);
                              }}
                            >
                              Send receipt
                            </Button>
                          </div>
                        </div>
                      </StudentCard>
                    ))}
                  </div>
                )}
              </StudentSection>

              <StudentSection
                title="Sent receipts"
                description="These have already been submitted and are waiting for review."
              >
                {!reviewPayments.length ? (
                  <StudentEmpty
                    title="No receipt waiting"
                    message="When you send a receipt, it will show here."
                  />
                ) : (
                  <div className="space-y-4">
                    {reviewPayments.map((payment) => (
                      <StudentCard key={payment.id} className="bg-white">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="space-y-2">
                            <p className="text-lg font-semibold text-stone-900">
                              {payment.courseName || 'Course payment'}
                            </p>
                            <p className="text-sm text-stone-500">
                              Sent {formatStudentDate(payment.paymentDate)}
                            </p>
                            <p className="text-sm text-stone-600">
                              Receipt number: {payment.receiptNumber || 'Not added'}
                            </p>
                          </div>
                          <StudentBadge tone="soft">Under review</StudentBadge>
                        </div>
                      </StudentCard>
                    ))}
                  </div>
                )}
              </StudentSection>
            </div>

            <div className="space-y-5">
              <StudentSection
                title="Payment history"
                description="Your approved receipts stay here for easy checking."
              >
                {!completedPayments.length ? (
                  <StudentEmpty
                    title="No completed payment yet"
                    message="Approved receipts will appear here."
                  />
                ) : (
                  <div className="space-y-3">
                    {completedPayments.map((payment) => (
                      <StudentCard key={payment.id} className="bg-stone-50">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-semibold text-stone-900">
                              {payment.courseName || 'Course payment'}
                            </p>
                            <StudentBadge tone="success">Approved</StudentBadge>
                          </div>
                          <p className="text-sm text-stone-500">
                            {formatStudentCurrency(payment.amount)}
                          </p>
                          <p className="text-sm text-stone-500">
                            {formatStudentDate(payment.paymentDate)}
                          </p>
                        </div>
                      </StudentCard>
                    ))}
                  </div>
                )}
              </StudentSection>

              <StudentSection
                title="Helpful links"
                description="Quick shortcuts for the next common task."
              >
                <div className="grid gap-3">
                  <StudentActionLink to="/courses/available">Browse courses</StudentActionLink>
                  <StudentActionLink to="/dashboard" variant="secondary">
                    Back to dashboard
                  </StudentActionLink>
                </div>
              </StudentSection>
            </div>
          </div>
        </StudentPage>

        <PayModal
          isOpen={isPayModalOpen}
          onClose={() => {
            setIsPayModalOpen(false);
            setSelectedPaymentRequest(null);
          }}
          paymentRequest={selectedPaymentRequest}
          onSuccess={() => {
            refresh();
            setIsPayModalOpen(false);
            setSelectedPaymentRequest(null);
          }}
        />
      </>
    );
  }

  return (
    <>
      <WorkspaceShell
        title="Payments Workspace"
        description={
          isAdmin
            ? 'Choose a payment queue from the rail, then review requests, proofs, and history in one operational workspace.'
            : 'Choose the payment queue you need, then pay, track approval, and review your completed receipts from one place.'
        }
        actions={
          isAdmin ? (
            <>
              <Button onClick={() => setIsPaymentRequestModalOpen(true)}>
                Create Request
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedPayment(null);
                  setIsRecordModalOpen(true);
                }}
              >
                Record Payment
              </Button>
            </>
          ) : null
        }
        sidebar={
          <WorkspaceRail
            title="Payment Queues"
            description="Select the queue you want to work through, then refine it with filters."
          >
            <WorkspaceSelectableList
              items={queueItems}
              selectedId={currentQueue?.id}
              onSelect={setSelectedQueueId}
              getKey={(item) => item.id}
              emptyTitle="No queues available"
              emptyMessage="Payments will appear here once requests or records exist."
              renderItem={(item) => (
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900">{item.label}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {item.description}
                      </p>
                    </div>
                    <WorkspaceStatusChip
                      status={item.status}
                      label={String(item.count)}
                    />
                  </div>
                </div>
              )}
            />

            <div className="space-y-2.5 rounded-[16px] border border-gray-200 bg-gray-50 p-3.5">
              <p className="text-sm font-medium text-gray-900">Filters</p>
              <input
                type="date"
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    startDate: event.target.value,
                  }))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              />
              <input
                type="date"
                value={filters.endDate}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    endDate: event.target.value,
                  }))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              />
              <input
                type="text"
                value={filters.studentName}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    studentName: event.target.value,
                  }))
                }
                placeholder="Filter by student"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              />
              <input
                type="text"
                value={filters.batchId}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    batchId: event.target.value,
                  }))
                }
                placeholder="Filter by batch"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              />
            </div>
          </WorkspaceRail>
        }
      >
        <WorkspaceStats>
          <WorkspaceStat
            label={isAdmin ? 'Pending Reviews' : 'Awaiting Review'}
            value={stats.pendingPayments}
            status="pending"
            hint="Receipts still waiting for a decision"
          />
          <WorkspaceStat
            label="Open Requests"
            value={stats.pendingRequests}
            status="planning"
            hint="Requests still waiting for payment"
          />
          <WorkspaceStat
            label={isAdmin ? 'Verified Payments' : 'Completed Payments'}
            value={stats.completedPayments}
            status="completed"
            hint="Finished payment records"
          />
          <WorkspaceStat
            label="Verified Amount"
            value={`LKR ${formatAmount(stats.totalAmount)}`}
            status="completed"
            hint="Completed payment total"
          />
        </WorkspaceStats>

        <WorkspaceSection
          title={currentQueue?.label || 'Payments'}
          description={currentQueue?.description}
          status={currentQueue?.status}
          statusLabel={`${currentQueue?.count || 0} items`}
        >
          <div className="space-y-6">
            {(currentQueue?.id === 'open-requests' || currentQueue?.id === 'history') && (
              <RequestList
                requests={currentRequests}
                isAdmin={isAdmin}
                onDelete={(request) => handleDelete(request, 'request')}
                onPay={(request) => {
                  setSelectedPaymentRequest(request);
                  setIsPayModalOpen(true);
                }}
              />
            )}

            {(currentQueue?.id === 'pending-verification' ||
              currentQueue?.id === 'awaiting-review' ||
              currentQueue?.id === 'history') && (
              <PaymentList
                payments={currentPayments}
                isAdmin={isAdmin}
                onEdit={handleEditPayment}
                onDelete={(payment) => handleDelete(payment, 'payment')}
                onVerify={(payment) => setPaymentToVerify(payment)}
              />
            )}

            {currentQueue?.id === 'open-requests' &&
              !currentRequests.length &&
              !currentPayments.length && (
                <WorkspaceEmptyState
                  title={isAdmin ? 'No open requests' : 'No payment requests waiting'}
                  message={
                    isAdmin
                      ? 'Create a new request when a student needs an invoice or installment reminder.'
                      : 'Once an admin issues a payment request, it will appear here.'
                  }
                />
              )}

            {['pending-verification', 'awaiting-review'].includes(currentQueue?.id) &&
              !currentPayments.length && (
                <WorkspaceEmptyState
                  title="No payments in this queue"
                  message="This review queue is clear right now."
                />
              )}

            {currentQueue?.id === 'history' &&
              !currentPayments.length &&
              !currentRequests.length && (
                <WorkspaceEmptyState
                  title="No payment history yet"
                  message="Completed and closed payment records will appear here."
                />
              )}
          </div>
        </WorkspaceSection>
      </WorkspaceShell>

      <RecordPaymentModal
        isOpen={isRecordModalOpen}
        onClose={() => {
          setIsRecordModalOpen(false);
          setSelectedPayment(null);
        }}
        onSuccess={() => {
          refresh();
          setIsRecordModalOpen(false);
          setSelectedPayment(null);
        }}
        paymentToEdit={selectedPayment}
      />

      <CreatePaymentRequestModal
        isOpen={isPaymentRequestModalOpen}
        onClose={() => setIsPaymentRequestModalOpen(false)}
        onSuccess={() => {
          refresh();
          setIsPaymentRequestModalOpen(false);
        }}
      />

      <PayModal
        isOpen={isPayModalOpen}
        onClose={() => {
          setIsPayModalOpen(false);
          setSelectedPaymentRequest(null);
        }}
        paymentRequest={selectedPaymentRequest}
        onSuccess={() => {
          refresh();
          setIsPayModalOpen(false);
          setSelectedPaymentRequest(null);
        }}
      />

      <VerifyPaymentModal
        isOpen={Boolean(paymentToVerify)}
        onClose={() => setPaymentToVerify(null)}
        payment={paymentToVerify}
        onVerify={(isApproved) => handleVerifyPayment(paymentToVerify, isApproved)}
      />
    </>
  );
};

const PaymentList = ({ payments, isAdmin, onEdit, onDelete, onVerify }) => {
  const pageSize = 10;
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(payments.length / pageSize));

  useEffect(() => {
    setPage(1);
  }, [payments]);

  if (!payments.length) return null;

  const safePage = Math.min(page, pageCount);
  const startIndex = (safePage - 1) * pageSize;
  const visiblePayments = payments.slice(startIndex, startIndex + pageSize);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <table className="w-full table-fixed divide-y divide-gray-200 text-sm">
        <colgroup>
          <col className="w-[19%]" />
          <col className="w-[18%]" />
          <col className="w-[11%]" />
          <col className="w-[12%]" />
          <col className="w-[11%]" />
          <col className="w-[12%]" />
          <col className="w-[9%]" />
          <col className="w-[8%]" />
        </colgroup>
        <thead className="bg-gray-50">
          <tr>
            {['Student', 'Course', 'Batch', 'Amount', 'Date', 'Type', 'Status', 'Actions'].map((heading) => (
              <th
                key={heading}
                className="truncate px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500"
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {visiblePayments.map((payment) => (
            <tr key={payment.id} className="hover:bg-stone-50">
              <td className="truncate px-3 py-2 font-semibold text-gray-900" title={payment.studentName || 'Unknown student'}>
                {payment.studentName || 'Unknown student'}
              </td>
              <td className="truncate px-3 py-2 text-gray-700" title={payment.courseName || '-'}>
                {payment.courseName || '-'}
              </td>
              <td className="truncate px-3 py-2 text-gray-700" title={payment.batchName || '-'}>
                {payment.batchName || '-'}
              </td>
              <td className="truncate px-3 py-2 text-gray-700">
                LKR {formatAmount(payment.amount)}
              </td>
              <td className="truncate px-3 py-2 text-gray-700">
                {formatDate(payment.paymentDate)}
              </td>
              <td className="truncate px-3 py-2 text-gray-700" title={payment.paymentType || '-'}>
                {payment.paymentType || '-'}
                {payment.installmentNumber
                  ? ` (${payment.installmentNumber}/${payment.totalInstallments})`
                  : ''}
              </td>
              <td className="truncate px-3 py-2">
                <span
                  className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getStatusBadgeClass(
                    payment.status,
                  )}`}
                >
                  {payment.status}
                </span>
              </td>
              <td className="px-3 py-2">
                {isAdmin ? (
                  payment.status === 'pending' ? (
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <IconActionButton
                        label="Verify payment"
                        tone="success"
                        onClick={() => onVerify(payment)}
                      >
                        <FaCheck className="h-3 w-3" />
                      </IconActionButton>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <IconActionButton
                        label="Edit payment"
                        tone="primary"
                        onClick={() => onEdit(payment)}
                      >
                        <FaPen className="h-3 w-3" />
                      </IconActionButton>
                      <IconActionButton
                        label="Delete payment"
                        tone="danger"
                        onClick={() => onDelete(payment)}
                      >
                        <FaTrash className="h-3 w-3" />
                      </IconActionButton>
                    </div>
                  )
                ) : (
                  '-'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {pageCount > 1 ? (
        <div className="flex flex-col gap-2 border-t border-gray-200 bg-white px-3 py-2 text-xs text-gray-600 sm:flex-row sm:items-center sm:justify-between">
          <span>
            Showing {startIndex + 1}-{Math.min(startIndex + pageSize, payments.length)} of {payments.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={safePage === 1}
              className="rounded-md border border-gray-200 px-2.5 py-1 font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>
            <span className="font-semibold text-gray-700">
              {safePage}/{pageCount}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              disabled={safePage === pageCount}
              className="rounded-md border border-gray-200 px-2.5 py-1 font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const RequestList = ({ requests, isAdmin, onDelete, onPay }) => {
  if (!requests.length) return null;

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {['Student', 'Course', 'Batch', 'Amount', 'Created', 'Due', 'Status', 'Actions'].map((heading) => (
              <th
                key={heading}
                className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500"
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {requests.map((request) => (
            <tr key={request.id} className="hover:bg-stone-50">
              <td className="whitespace-nowrap px-3 py-2 font-semibold text-gray-900">
                {request.studentName || 'Student'}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                {request.courseName || '-'}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                {request.batchName || '-'}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                LKR {formatAmount(request.amount)}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                {formatDate(request.createdAt)}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                {formatDate(request.dueDate)}
              </td>
              <td className="whitespace-nowrap px-3 py-2">
                <WorkspaceStatusChip status={request.status} label={request.status} />
              </td>
              <td className="whitespace-nowrap px-3 py-2">
                {isAdmin ? (
                  <div className="flex items-center gap-1.5 whitespace-nowrap">
                    <IconActionButton
                      label="Delete request"
                      tone="danger"
                      onClick={() => onDelete(request)}
                    >
                      <FaTrash className="h-3 w-3" />
                    </IconActionButton>
                  </div>
                ) : request.status === 'pending' ? (
                  <div className="flex items-center gap-1.5 whitespace-nowrap">
                    <IconActionButton
                      label="Pay now"
                      tone="primary"
                      onClick={() => onPay(request)}
                    >
                      <FaCreditCard className="h-3 w-3" />
                    </IconActionButton>
                  </div>
                ) : (
                  '-'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const IconActionButton = ({ children, label, onClick, tone = 'primary' }) => {
  const toneClass = {
    primary: 'text-pink-700 ring-pink-100 hover:bg-pink-50 hover:text-pink-900',
    danger: 'text-red-700 ring-red-100 hover:bg-red-50 hover:text-red-900',
    success: 'text-emerald-700 ring-emerald-100 hover:bg-emerald-50 hover:text-emerald-900',
  }[tone];

  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white ring-1 transition ${toneClass}`}
    >
      {children}
    </button>
  );
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString() : 'Not set';

const PaymentInfo = ({ label, value }) => (
  <div className="rounded-2xl bg-stone-50 px-4 py-3">
    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
      {label}
    </p>
    <p className="mt-1 text-sm font-semibold text-stone-900">{value}</p>
  </div>
);

export default PaymentsPage;
